import { useAuth0 } from '@auth0/auth0-react';
import {
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BoltIcon,
  ChartPieIcon,
  ClockIcon,
  CursorArrowRaysIcon,
  EyeIcon,
  NoSymbolIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import THSHeroImg from '../assets/THS-Hero-IMG.png';
import value2 from '../assets/booking-capablities.png';
import value3 from '../assets/reports.png';
import rosterTableImg from '../assets/roster-table.png';
import THSTrust1 from '../assets/ths-trust-1.png';
import THSTrust2 from '../assets/ths-trust-2.png';
import THSTrust3 from '../assets/ths-trust-3.png';
import THSTrust4 from '../assets/ths-trust-4.png';
import THSTrust5 from '../assets/ths-trust-5.png';
import value1 from '../assets/value-1.png';
import { Button } from '../components/Button';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { getRememberedClient } from '../hotel/rememberedClient';

const features = [
  {
    name: 'Real-time Roster Management',
    description:
      'Obtain real-time visibility into all your reservations, knowing exactly where your employees are, along with their check-in and check-out details.',
    href: '#',
    icon: UserGroupIcon,
    img: value1,
  },
  {
    name: 'Effortless Booking with a Personal Touch',
    description:
      'Book and request changes to your reservations effortlessly. Our digital platform combines seamless efficiency with concierge-level service, ensuring personalized support for every booking.',
    href: '#',
    icon: CursorArrowRaysIcon,
    img: value2,
  },
  {
    name: 'Instantly Access Comprehensive Reports',
    description:
      'Gain immediate access to detailed reports that offer a full overview of all your reservations, equipping you with the insights and convenience necessary for effective management.',
    href: '#',
    icon: BoltIcon,
    img: value3,
  },
];

const stats = [
  { label: 'Years of Experience', value: '28' },
  { label: 'Countries', value: '30+' },
  { label: 'Nights Booked', value: '8 Million' },
];

const highlights = [
  {
    name: 'Reduced Lodging Budgets',
    description: 'Through negotiated rates with our global network partners.',
    icon: BanknotesIcon,
  },
  {
    name: 'No Administrative Hassle',
    description:
      "Because we coordinate your personnel's lodging logistics directly.",
    icon: NoSymbolIcon,
  },
  {
    name: 'Dedicated Support 24/7',
    description:
      'With rate negotiation, reservations, security, consolidated billing, turnaround, crew rosters, and reporting support.',
    icon: ClockIcon,
  },
  {
    name: 'Knowing Who Is Staying Where',
    description: 'At all times with real-time roommate rosters and reporting.',
    icon: EyeIcon,
  },
  {
    name: 'Easier Expense Tracking',
    description:
      'With consolidating billing across multiple projects or locations.',
    icon: ChartPieIcon,
  },
  {
    name: 'Reservation Flexibility',
    description:
      'With flexible options on every stay in case you need to stay longer than expected or for the early check-outs.',
    icon: ArrowsRightLeftIcon,
  },
];

export default function Landing() {
  const { loginWithRedirect, isAuthenticated, isLoading, user } = useAuth0();
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard unless they explicitly want to view the public page
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const searchParams = new URLSearchParams(window.location.search);
      if (!searchParams.has('public')) {
        const clientId = getRememberedClient(user?.sub);
        navigate(
          clientId
            ? `/clients/${encodeURIComponent(clientId)}/dashboard`
            : '/select-client',
          { replace: true },
        );
      }
    }
  }, [isAuthenticated, isLoading, navigate, user?.sub]);

  const handleLogin = () => {
    // Remove the public parameter when logging in to ensure normal flow
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete('public');
    window.history.replaceState(
      {},
      '',
      currentUrl.pathname + currentUrl.search,
    );
    loginWithRedirect();
  };

  return (
    <div className="flex flex-col">
      <Header />

      <div className="relative isolate overflow-hidden bg-gradient-to-b from-gray-100/20 pt-0 md:pt-14">
        <div
          className="absolute inset-y-0 right-1/2 -z-10 -mr-96 w-[200%] origin-top-right skew-x-[-30deg] bg-white shadow-xl shadow-gray-600/10 ring-1 ring-gray-50 sm:-mr-80 lg:-mr-96"
          aria-hidden="true"
        />
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 xl:grid-cols-1 xl:grid-rows-1 xl:gap-x-8">
            <h1 className="max-w-2xl text-4xl font-bold text-gray-900 sm:text-6xl lg:col-span-2 xl:col-auto">
              Instant Access to Your Housing Needs with Our New Client
              Portal—Anytime, Anywhere
            </h1>
            <div className="mt-6 max-w-xl lg:mt-0 xl:col-end-1 xl:row-start-1">
              <p className="text-lg leading-8 text-gray-600">
                Discover our new client portal—a streamlined solution for
                managing your reservations with enhanced transparency and
                control. Directly request bookings, adjust existing
                reservations, and view live rosters, all from one convenient
                location.
              </p>
              <div className="mt-10 flex items-center gap-x-4">
                <Button
                  className="bg-accentGreen px-3.5 py-2.5 text-base font-semibold text-white hover:bg-secondaryBlue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondaryBlue"
                  onClick={handleLogin}
                >
                  Client Login
                </Button>
                <a
                  href="https://www.teamhousing.com/contact/"
                  className="rounded-md bg-primaryBlue px-3.5 py-2.5 text-base font-semibold text-white shadow-sm hover:bg-secondaryBlue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondaryBlue"
                >
                  Request Early Access
                </a>
              </div>
            </div>
            <img
              src={THSHeroImg}
              alt=""
              className="mt-10 aspect-[6/5] w-full max-w-lg rounded-2xl object-contain sm:mt-16 lg:mt-0 lg:max-w-none xl:row-span-2 xl:row-end-2 xl:mt-36"
            />
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-white sm:h-32" />
      </div>

      <div className="overflow-hidden bg-gray-50 py-16 md:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:items-center">
            <div className="lg:pr-4 lg:pt-4">
              <div className="lg:max-w-lg">
                <p className="mt-2 text-3xl font-bold text-gray-900 sm:text-4xl">
                  Experience Our New Client Portal, Backed by 24/7, 365 Days
                  Dedicated Support
                </p>
                <hr className="my-8 h-1 w-48 border-0 bg-accentGreen"></hr>
                <p className="mt-6 text-lg leading-8 text-gray-600">
                  Our new platform comes with the trusted expertise of our
                  service team, available 24/7. From rate negotiation and
                  reservations to security, consolidated billing, and detailed
                  reporting, we handle it all.
                </p>
                <p className="mt-4 text-lg leading-8 text-gray-600">
                  Count on us for comprehensive support in managing your crew
                  rosters and turnaround needs, ensuring every aspect of your
                  travel is covered.
                </p>
              </div>
            </div>
            <img
              src={rosterTableImg}
              alt="Product screenshot"
              className="w-[48rem] max-w-none rounded-xl bg-gray-100 px-4 py-8 shadow-xl sm:w-[57rem] md:-ml-4 lg:ml-0"
              width={2432}
              height={1442}
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-50 py-24 sm:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <p className="mt-2 text-center text-3xl font-bold text-gray-900 sm:text-4xl">
              Seamless Booking and Real-Time Insights in One Interface
            </p>
          </div>
          <hr className="mx-auto mb-0 mt-8 h-1 w-48 border-0 bg-accentGreen"></hr>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {features.map((feature) => (
                <div key={feature.name} className="flex flex-col">
                  <img
                    src={feature.img}
                    alt="Product screenshot"
                    className="mb-8 rounded-xl bg-gray-100 p-4 shadow-md"
                  />
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                    <feature.icon
                      className="size-5 flex-none text-accentGreen"
                      aria-hidden="true"
                    />
                    {feature.name}
                  </dt>
                  <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">{feature.description}</p>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      <div className="relative bg-gray-100 py-24 sm:py-32 lg:py-40">
        <div className="mx-auto max-w-md px-6 text-center sm:max-w-3xl lg:max-w-7xl lg:px-8">
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Experience the THS Difference
          </p>
          <hr className="mx-auto my-8 h-1 w-48 border-0 bg-accentGreen"></hr>
          <div className="mt-4 md:mt-20">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-12 lg:grid-cols-3">
              {highlights.map((highlight) => (
                <div key={highlight.name} className="pt-6">
                  <div className="flow-root h-full rounded-lg bg-gray-50 px-6 pb-8">
                    <div className="mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center rounded-xl bg-accentGreen p-3 shadow-lg">
                          <highlight.icon
                            className="size-8 text-white"
                            aria-hidden="true"
                          />
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-semibold leading-8 tracking-tight text-gray-900">
                        {highlight.name}
                      </h3>
                      <p className="mt-2 text-base leading-7 text-gray-600">
                        {highlight.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white pb-10">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto mt-10 grid max-w-lg grid-cols-2 items-center gap-x-20 gap-y-10 sm:max-w-xl md:grid-cols-5 lg:mx-0 lg:max-w-none">
            <img
              className="col-span-1 w-full object-contain"
              src={THSTrust1}
              alt="GSA"
              width={200}
              height={200}
            />
            <img
              className="col-span-1 w-full object-contain"
              src={THSTrust2}
              alt="Women Owned"
              width={200}
              height={200}
            />
            <img
              className="col-span-1 w-full object-contain"
              src={THSTrust3}
              alt="America's Fastest Growing Private Companies INC 5000"
              width={200}
              height={200}
            />
            <img
              className="col-span-1 w-full object-contain"
              src={THSTrust4}
              alt="Business Executives for National Security"
              width={200}
              height={200}
            />
            <img
              className="col-span-1 w-full object-contain"
              src={THSTrust5}
              alt="Aggie 100 2022 Honoree"
              width={200}
              height={200}
            />
          </div>
        </div>
      </div>

      <div className="bg-primaryBlue py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
            <h2 className="text-3xl font-bold tracking-tight text-accentGreen sm:text-4xl">
              Why Trust Us?
            </h2>
            <div className="mt-6 flex flex-col gap-x-8 gap-y-20 lg:flex-row">
              <div className="lg:w-full lg:max-w-2xl lg:flex-auto">
                <p className="text-xl leading-8 text-white">
                  Team Housing Solutions® provides lodging solutions for
                  business and government travelers worldwide. We have roots as
                  a service provider for the government and military, so we
                  understand your precise needs for your accommodations.
                </p>
                <hr className="mb-0 mt-8 h-1 w-48 border-0 bg-accentGreen"></hr>
                <div className="flex flex-col gap-y-4">
                  <div className="w-full pt-8">
                    <figure className="rounded-2xl bg-gray-900 p-8 text-base leading-6">
                      <blockquote className="text-gray-200">
                        <p>
                          “Team Housing went above and beyond to make sure our
                          employees were happy with their accommodations. I
                          would highly recommend this group for your corporate
                          housing needs.”
                        </p>
                      </blockquote>
                      <figcaption className="mt-6 flex items-center gap-x-4">
                        <div>
                          <div className="font-semibold text-accentGreen">
                            Larry W.
                          </div>
                          <div className="text-gray-400">Organization Name</div>
                        </div>
                      </figcaption>
                    </figure>
                  </div>
                  <div className="w-full">
                    <figure className="rounded-2xl bg-gray-900 p-8 text-base leading-6">
                      <blockquote className="text-gray-200">
                        <p>
                          “I would highly recommend them for anyone seeking a
                          furnished apartment with all the necessities for
                          temporary living quarters. Polite, courteous, and
                          professional follow-up service!”
                        </p>
                      </blockquote>
                      <figcaption className="mt-6 flex items-center gap-x-4">
                        <div>
                          <div className="font-semibold text-accentGreen">
                            Allen A.
                          </div>
                          <div className="text-gray-400">Organization Name</div>
                        </div>
                      </figcaption>
                    </figure>
                  </div>
                </div>
              </div>
              <div className="lg:flex lg:flex-auto lg:justify-center">
                <dl className="w-64 space-y-8 xl:w-80">
                  {stats.map((stat) => (
                    <div
                      key={stat.label}
                      className="flex flex-col-reverse gap-y-2"
                    >
                      <dt className="text-base leading-7 text-gray-400">
                        {stat.label}
                      </dt>
                      <dd className="text-5xl font-semibold tracking-tight text-accentGreen">
                        {stat.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
